import React, { useState } from "react";
import { Users, Zap } from "lucide-react";
import type { ProductSkuId } from "@limeshare/shared";

export function TeamCostCalculator({ onSelectSku }: { onSelectSku: (sku: ProductSkuId) => void }) {
  const [seats, setSeats] = useState(15);

  const wetransferYearly = seats * 19 * 12; // $19/seat/mo
  const dropboxYearly = seats * 24 * 12; // $24/seat/mo
  const frameioYearly = seats * 19 * 12; // $19/seat/mo
  const limeshareTeams = seats <= 25 ? 149 : 298; // Flat $149 per 25 seats
  const savedVsWeTransfer = Math.max(0, wetransferYearly - limeshareTeams);

  return (
    <section className="panel" style={{ marginBottom: 48 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
        <div>
          <span className="badge" style={{ marginBottom: 8 }}>
            <Users size={12} /> Interactive B2B Seat Tax Calculator
          </span>
          <h2 style={{ margin: "0 0 4px", fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 900 }}>
            How Much Does Your Team Overpay for Per-Seat Subscriptions?
          </h2>
          <p className="hint" style={{ margin: 0 }}>
            Slide to select your team size and compare annual per-seat costs against LimeShare Teams ($149 flat for 25 seats).
          </p>
        </div>

        <div style={{ textAlign: "right" }}>
          <span className="hint" style={{ fontSize: 12, display: "block" }}>Your Annual Savings</span>
          <b style={{ fontSize: 32, fontWeight: 900, color: "var(--lime)" }}>${savedVsWeTransfer.toLocaleString()}/yr</b>
        </div>
      </div>

      {/* Team Size Slider */}
      <div style={{ padding: 18, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 12, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <b style={{ fontSize: 14 }}>Team Members: <span style={{ color: "var(--lime)" }}>{seats} people</span></b>
          <span className="hint" style={{ fontSize: 12 }}>2 TB Shared Encrypted Pool Included</span>
        </div>
        <input
          type="range"
          min={3}
          max={50}
          value={seats}
          onChange={(e) => setSeats(Number(e.target.value))}
          style={{ width: "100%", accentColor: "var(--lime)", cursor: "pointer" }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
        <div style={{ padding: 16, borderRadius: 12, background: "var(--panel-2)", border: "1px solid var(--border)" }}>
          <small className="hint">WeTransfer Teams ({seats} seats)</small>
          <div style={{ fontSize: 24, fontWeight: 800, margin: "4px 0" }}>${wetransferYearly.toLocaleString()}/yr</div>
          <small className="hint" style={{ fontSize: 11 }}>$19 per user / month</small>
        </div>

        <div style={{ padding: 16, borderRadius: 12, background: "var(--panel-2)", border: "1px solid var(--border)" }}>
          <small className="hint">Dropbox Business ({seats} seats)</small>
          <div style={{ fontSize: 24, fontWeight: 800, margin: "4px 0" }}>${dropboxYearly.toLocaleString()}/yr</div>
          <small className="hint" style={{ fontSize: 11 }}>$24 per user / month</small>
        </div>

        <div style={{ padding: 16, borderRadius: 12, background: "var(--panel-2)", border: "1px solid var(--border)" }}>
          <small className="hint">Frame.io Pro ({seats} seats)</small>
          <div style={{ fontSize: 24, fontWeight: 800, margin: "4px 0" }}>${frameioYearly.toLocaleString()}/yr</div>
          <small className="hint" style={{ fontSize: 11 }}>$19 per user / month</small>
        </div>

        <div style={{ padding: 16, borderRadius: 12, background: "var(--lime)", color: "var(--btn-text)" }}>
          <small style={{ fontWeight: 700, opacity: 0.9 }}>LimeShare Teams (Up to {seats <= 25 ? 25 : 50} seats)</small>
          <div style={{ fontSize: 26, fontWeight: 900, margin: "4px 0" }}>${limeshareTeams}/yr</div>
          <button
            type="button"
            onClick={() => onSelectSku("teams-25")}
            style={{ marginTop: 6, width: "100%", padding: "6px 10px", borderRadius: 8, border: 0, background: "rgba(0,0,0,0.22)", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
          >
            <Zap size={12} style={{ verticalAlign: "middle" }} /> Lock Flat Rate
          </button>
        </div>
      </div>
    </section>
  );
}
